import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getProductsSerials } from "../../../components/products/functions";
import {
  downloadBlob,
  saveModelsTags,
  getSerialsByNumbers,
  saveManualTagEmissions,
} from "../../../components/serials/functions";
import { buildApplicableGradePayload } from "../../../components/utils/serialGradeFields";
import ToastContext from "../../../context/toastProvider";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileStickyActions from "../../ui/MobileStickyActions";
import { MobileTagErrorSheet, MobileTagInfosSheet } from "./MobileTagSheets";
import {
  canGenerateTags,
  enterSelectionMode,
  exitSelectionMode,
  getSelectedTags,
  toggleCardSelect,
  toggleSelectAll,
  updateTagField,
} from "./tagSelectionRules";
import { focusFirstTagErrorField } from "../../../components/serials/tags/focusFirstTagError";
import { Sentry } from "../../../sentry";
import { buildTagsZip } from "../../../components/serials/tags/buildTagsZip";
import { formatLocalDate, zipDownloadName } from "../../../components/serials/tags/tagZipNames";
import {
  buildPartitionErrorData,
  buildTagsSendData,
  buildTagSavePayload,
  buildManualAuditPayload,
  applySavedTagsToSerials,
  cardFromSerial,
} from "../../../components/serials/tags/submitTagsFlow";
import { validateSelectedTags } from "../../../components/serials/tags/validateTagsLive";
import LabelCardEditor from "../../../components/serials/tags/LabelCardEditor";
import { enrichCardLive } from "../../../components/serials/tags/enrichCardLive";
import { templateBucket } from "../../../components/serials/tags/labelTemplates";
import { isDouradoEligible } from "../../../components/serials/tags/partitionByPaper";
import {
  filterPrefilledOverflows,
  overflowKeysBySerial,
  snapshotTagFields,
} from "../../../components/serials/tags/limitString";
import {
  preloadTagFonts,
  tagFontsReady,
} from "../../../components/serials/tags/preloadTagFonts";
import TagsSelectionHub from "../../../components/serials/tags/TagsSelectionHub";
import ManualTagsWizard from "../../../components/serials/tags/ManualTagsWizard";
import { mergeResolvedSerials } from "../../../components/serials/tags/resolveTagsSelection";
import "../../../components/serials/tags/tagFonts.css";

const actionBtnClass =
  "inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao";
const primaryBtnClass =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-green-700 px-4 text-sm font-medium text-white hover:bg-green-800 disabled:opacity-50";

const buildCardsFromSerials = (serialList) =>
  (serialList || []).map((s) => {
    const card = cardFromSerial(
      s,
      buildApplicableGradePayload(s, s?.product || s?.productData)
    );
    if (isDouradoEligible(card)) {
      card.invertida = false;
    }
    return card;
  });

export default function MobileTagGenerator({ user }) {
  const location = useLocation();
  const products = location.state?.products;
  const hasProductsFromNav = Boolean(products?.length);
  const { goodToast, badToast } = useContext(ToastContext);

  const [phase, setPhase] = useState(() => (hasProductsFromNav ? "generator" : "hub"));
  const [serials, setSerials] = useState([]);
  const [missing, setMissing] = useState([]);
  const [tagsData, setTagsData] = useState([]);
  const [initialBySerial, setInitialBySerial] = useState({});
  const [loading, setLoading] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [errorSheet, setErrorSheet] = useState(null);
  const [infosModel, setInfosModel] = useState(null);
  const [fontsReady, setFontsReady] = useState(() => tagFontsReady());
  const [unifyPages, setUnifyPages] = useState(false);

  useEffect(() => {
    let cancelled = false;
    preloadTagFonts()
      .then(() => {
        if (!cancelled) setFontsReady(true);
      })
      .catch(() => {
        /* ignore: font optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hasProductsFromNav) return undefined;
    const loadSerials = async () => {
      setLoading(true);
      const data = await getProductsSerials(user?.token, products);
      if (data?.type === "success") {
        setSerials(data?.data ?? []);
      } else {
        setSerials([]);
      }
      setLoading(false);
    };
    loadSerials();
  }, [user?.token, products, hasProductsFromNav]);

  useEffect(() => {
    if (phase !== "generator" || !serials?.length) return;
    setTagsData((prev) => {
      const manuals = (prev || []).filter((c) => c?.source === "manual");
      const fromDb = buildCardsFromSerials(serials);
      const manualSerials = new Set(manuals.map((m) => m.serial));
      const merged = [
        ...fromDb.filter((c) => !manualSerials.has(c.serial)),
        ...manuals,
      ];
      setInitialBySerial(
        Object.fromEntries(merged.map((c) => [c.serial, snapshotTagFields(c)]))
      );
      return merged;
    });
  }, [serials, phase]);

  useEffect(() => {
    if (!tagsData?.length) {
      setErrorData(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      try {
        const next = validateSelectedTags(tagsData);
        setErrorData(next.cards.length ? next : null);
      } catch (error) {
        setErrorData(buildPartitionErrorData(error));
      }
    }, 200);
    return () => window.clearTimeout(timer);
  }, [tagsData, fontsReady]);

  const highlightErrorData = useMemo(
    () => filterPrefilledOverflows(errorData || { cards: [] }, tagsData, initialBySerial),
    [errorData, tagsData, initialBySerial]
  );

  useEffect(() => {
    if (!highlightErrorData?.cards?.length) return undefined;
    const timer = window.setTimeout(() => {
      focusFirstTagErrorField(highlightErrorData);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [highlightErrorData]);

  const handleContinue = async ({ productIds, serialNumbers }) => {
    setLoading(true);
    let fromProducts = [];
    if (productIds?.length) {
      const data = await getProductsSerials(user?.token, productIds);
      if (data?.type === "success") {
        fromProducts = data?.data || [];
      } else {
        badToast(data?.message || "Erro ao buscar seriais dos pedidos");
        setLoading(false);
        return;
      }
    }

    let fromLookup = { found: [], missing: [] };
    if (serialNumbers?.length) {
      const data = await getSerialsByNumbers(user?.token, serialNumbers);
      if (data?.type === "success") {
        fromLookup = {
          found: data.found || [],
          missing: data.missing || [],
        };
      } else {
        badToast(data?.message || "Erro no lookup de seriais");
        setLoading(false);
        return;
      }
    }

    const { serials: merged, missing: nextMissing } = mergeResolvedSerials({
      fromProducts,
      fromLookup,
    });
    setSerials(merged);
    setMissing(nextMissing);
    setPhase(nextMissing.length ? "wizard" : "generator");
    setLoading(false);
  };

  const handleWizardComplete = (manualCards) => {
    const dbCards = buildCardsFromSerials(serials);
    if (!serials?.length && !manualCards?.length) {
      badToast("Nenhum serial selecionado");
      setPhase("hub");
      setSerials([]);
      setMissing([]);
      setTagsData([]);
      return;
    }
    const merged = [...dbCards, ...(manualCards || [])];
    setTagsData(merged);
    setInitialBySerial(
      Object.fromEntries(merged.map((c) => [c.serial, snapshotTagFields(c)]))
    );
    setPhase("generator");
  };

  const handleWizardCancel = () => {
    setPhase("hub");
    setMissing([]);
  };

  const enableSelection = () => {
    setSelectionMode(true);
    setTagsData((current) => enterSelectionMode(current));
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setTagsData((current) => exitSelectionMode(current));
  };

  const handleToggleSelectAll = () => {
    setTagsData((current) => toggleSelectAll(current));
  };

  const handleFieldChange = (serialNumber, field, value) => {
    setTagsData((current) => updateTagField(current, serialNumber, field, value));
  };

  const handleSelectChange = (serialNumber, selected) => {
    setTagsData((current) => toggleCardSelect(current, serialNumber, selected));
  };

  const submitTags = async () => {
    try {
      setGenerationLoading(true);
      const liveErrors = validateSelectedTags(tagsData);
      if (liveErrors.cards.length) {
        setErrorData(liveErrors);
        badToast("Corrija os campos que excedem o limite antes de baixar");
        return;
      }
      setErrorData(null);
      const sendData = buildTagsSendData(tagsData);
      const savePayload = buildTagSavePayload(tagsData, serials);

      if (savePayload.length) {
        const response = await saveModelsTags(user?.token, savePayload);
        if (response?.type === "error") {
          if (response?.data) {
            setErrorData(response.data);
          }
          badToast(response?.message || "Erro ao salvar etiquetas");
          return;
        }
        setSerials((prev) => applySavedTagsToSerials(prev, savePayload));
      }

      const auditPayload = buildManualAuditPayload(tagsData);
      if (auditPayload.length) {
        const manualResponse = await saveManualTagEmissions(user?.token, auditPayload);
        if (manualResponse?.type === "error") {
          badToast(manualResponse?.message || "Erro ao registrar etiquetas manuais");
          return;
        }
      }

      const date = formatLocalDate();
      const { blob } = await buildTagsZip(sendData, { unifyPages, date });
      downloadBlob(blob, zipDownloadName(date));
      goodToast("Etiquetas geradas e salvas!");
    } catch (error) {
      Sentry.captureException(error, { extra: { area: "tags", action: "mobileSubmitTags" } });
      const partitionError = buildPartitionErrorData(error);
      if (partitionError) {
        setErrorData(partitionError);
        badToast(error?.message || "Erro na geração de etiquetas");
        return;
      }
      badToast(error?.message || "Erro na geração de etiquetas");
    } finally {
      setGenerationLoading(false);
    }
  };

  if (phase === "hub") {
    return (
      <div className="flex flex-col pb-24">
        <div className="border-b border-primary/30 bg-mgs-bg px-4 py-3 text-center">
          <h2 className="text-lg font-semibold text-laranjinha">Gerador de etiquetas</h2>
        </div>
        <div className="px-2 mt-2">
          <TagsSelectionHub user={user} onContinue={handleContinue} />
        </div>
        {loading ? (
          <div className="px-4 py-4" aria-label="Carregando seriais" role="status">
            <MobileSkeleton lines={5} />
          </div>
        ) : null}
      </div>
    );
  }

  if (phase === "wizard") {
    return (
      <div className="flex flex-col pb-24">
        <div className="border-b border-primary/30 bg-mgs-bg px-4 py-3 text-center">
          <h2 className="text-lg font-semibold text-laranjinha">Gerador de etiquetas</h2>
        </div>
        <div className="px-2 mt-2">
          <ManualTagsWizard
            missing={missing}
            onComplete={handleWizardComplete}
            onCancel={handleWizardCancel}
            fontsReady={fontsReady}
          />
        </div>
        {loading ? (
          <div className="px-4 py-4" aria-label="Carregando seriais" role="status">
            <MobileSkeleton lines={5} />
          </div>
        ) : null}
      </div>
    );
  }

  const selectedCount = getSelectedTags(tagsData).length;
  const allSelected =
    tagsData?.length > 0 && selectedCount === tagsData.length;
  const hasBlockingErrors = (errorData?.cards?.length || 0) > 0;
  const canDownload = canGenerateTags(tagsData) && !hasBlockingErrors && !generationLoading;

  const overflowBySerial = overflowKeysBySerial(highlightErrorData);
  const rows = tagsData.map(enrichCardLive);

  return (
    <div className="flex flex-col pb-24">
      <div className="border-b border-primary/30 bg-mgs-bg px-4 py-3 text-center">
        <h2 className="text-lg font-semibold text-laranjinha">Gerador de etiquetas</h2>
        <p className="mt-1 text-sm text-laranjinha/70">
          {serials.length
            ? `${selectedCount} de ${tagsData.length} carta(s) selecionada(s)`
            : tagsData.length
              ? `${selectedCount} de ${tagsData.length} carta(s) selecionada(s)`
              : "Carregando seriais…"}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 px-4 py-3">
        {selectionMode ? (
          <>
            <button type="button" onClick={cancelSelection} className={actionBtnClass}>
              Cancelar seleção
            </button>
            <button type="button" onClick={handleToggleSelectAll} className={actionBtnClass}>
              {allSelected ? "Limpar seleção" : "Selecionar todas"}
            </button>
          </>
        ) : (
          <button type="button" onClick={enableSelection} className={actionBtnClass}>
            Habilitar seleção
          </button>
        )}
      </div>

      {loading ? (
        <div className="px-4 py-4" aria-label="Carregando seriais" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : !serials.length && !tagsData.length ? (
        <MobileEmpty
          title="Sem seriais"
          message="Nenhum serial registrado neste pedido."
        />
      ) : (
        <>
          {hasBlockingErrors ? (
            <p className="px-4 pb-2 text-sm text-red-500">
              Textos pré-preenchidos acima do limite — ao editar, o campo não aceita mais do que
              cabe.
            </p>
          ) : null}
          <div className="space-y-4 px-2">
            {rows.map(({ card, bucket, error }, index) => {
              const serial = serials.find((s) => s?.serialNumber === card.serial);
              const douradoEligible = isDouradoEligible(card);
              const overflowKeys = overflowBySerial[card.serial];
              const dimmed = selectionMode && !card.select;
              return (
                <div
                  key={card.serial ?? index}
                  className="rounded-lg border border-primary/30 bg-white p-2"
                  style={{ opacity: dimmed ? 0.45 : 1 }}
                >
                  <div className="mb-2 flex items-center gap-2 text-sm text-black">
                    {selectionMode ? (
                      <input
                        type="checkbox"
                        checked={card.select === true}
                        onChange={(event) =>
                          handleSelectChange(card.serial, event.target.checked)
                        }
                        aria-label={`Selecionar ${card.serial}`}
                      />
                    ) : null}
                    <span className="text-gray-500">{index + 1}.</span>
                    <span className="font-mono text-xs">{card.serial}</span>
                    {bucket ? (
                      <span className="text-xs text-gray-500">{templateBucket(bucket).label}</span>
                    ) : null}
                    {!douradoEligible ? (
                      <label className="ml-auto flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={card.invertida === true}
                          onChange={(event) =>
                            handleFieldChange(card.serial, "invertida", event.target.checked)
                          }
                          aria-label={`Invertida ${card.serial}`}
                        />
                        <span className="text-xs">Invertida</span>
                      </label>
                    ) : null}
                    {serial?.gradableModel ? (
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={() => setInfosModel(serial.gradableModel)}
                      >
                        info
                      </button>
                    ) : null}
                  </div>

                  {error ? (
                    <p className="px-2 py-4 text-sm text-red-600">
                      Subgrades incompletas — preencha as quatro notas ou deixe todas em branco.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <LabelCardEditor
                        key={`label-${card.serial}-${fontsReady ? "fonts" : "nofonts"}`}
                        card={card}
                        scale={1.75}
                        overflowKeys={overflowKeys}
                        readOnly={dimmed}
                        onField={(field, value) =>
                          handleFieldChange(card.serial, field, value)
                        }
                      />
                    </div>
                  )}

                  {overflowKeys?.size ? (
                    <button
                      type="button"
                      className="mt-1 text-xs text-red-600 underline"
                      onClick={() =>
                        setErrorSheet(
                          highlightErrorData?.cards?.find((c) => c.serial === card.serial) || null
                        )
                      }
                    >
                      Ver campos que excedem
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tagsData.length ? (
        <MobileStickyActions>
          <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={unifyPages}
              onChange={(e) => setUnifyPages(e.target.checked)}
            />
            Unificar páginas
          </label>
          <button
            type="button"
            disabled={!canDownload}
            onClick={submitTags}
            className={primaryBtnClass}
          >
            {generationLoading ? "Gerando…" : "Baixar ZIP"}
          </button>
        </MobileStickyActions>
      ) : null}

      <MobileTagErrorSheet
        open={Boolean(errorSheet)}
        onClose={() => setErrorSheet(null)}
        data={errorSheet}
      />

      <MobileTagInfosSheet
        open={Boolean(infosModel)}
        onClose={() => setInfosModel(null)}
        model={infosModel}
      />

      {generationLoading ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          role="status"
          aria-label="Gerando etiquetas"
        >
          <div className="rounded-lg bg-mgs-bg px-6 py-4 text-sm font-medium text-laranjinha">
            Gerando etiquetas…
          </div>
        </div>
      ) : null}
    </div>
  );
}
